import { isEqual, isEmpty, concat, filter } from 'lodash';
import turf from '@turf/turf';
import toGeoJSON from 'togeojson';

import { readUploadedFileAsText } from '../export-import-files';

/**
 * Takes a kml file and gets the first LineString to create the track for the video.
 *
 * @async
 * @param {File} file - The kml file to read.
 * @returns {object} An object containing the array of feature Points, a feature LineString representing the video track
 * and a Feature Collection of Points with the control points.
 * @throws {Error} Error if anything goes wrong or file is not kml.
 */
const createTrackFromKML = async file => {
  const fileExtension = file.name.substring(file.name.lastIndexOf('.') + 1, file.name.length);

  try {
    if (!isEqual(fileExtension, 'kml'))
      if (!isEqual(fileExtension, 'KML')) throw new Error('No valid file extension');

    const kmlText = await readUploadedFileAsText(file);

    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(kmlText, 'text/xml');
    const featureCollectionFromKML = toGeoJSON.kml(xmlDoc);

    let newTrackCoordinates = [];
    turf.featureEach(featureCollectionFromKML, currentFeature => {
      if (turf.getType(currentFeature) === 'LineString') {
        const lineCoordinates = turf.getCoords(currentFeature);
        const lastTrackCoordinates = newTrackCoordinates.pop();

        if (isEmpty(newTrackCoordinates) || isEqual(lineCoordinates[0], lastTrackCoordinates))
          newTrackCoordinates = concat(newTrackCoordinates, lineCoordinates);
        else throw new Error('Too many line geometries in this file');
      }
    });
    const newTrack = turf.lineString(newTrackCoordinates);

    const newFeatureArray = [];
    turf.coordEach(newTrack, (currentCoord, coordIndex) => {
      let newCoord = currentCoord;
      if (currentCoord.length < 3) newCoord = [...currentCoord, 0];
      const newFeaturePoint = turf.point(newCoord, {
        distanceTravelled: 0,
        isControlPoint: coordIndex === 0 || coordIndex === newTrackCoordinates.length - 1,
        timeUS: 0
      });
      newFeatureArray.push(newFeaturePoint);
    });

    const newControlPoints = turf.featureCollection(
      filter(newFeatureArray, ['properties.isControlPoint', true])
    );

    if (!isEmpty(newTrackCoordinates)) return { newFeatureArray, newTrack, newControlPoints };

    throw new Error('No valid geometries in this file');
  } catch (error) {
    throw new Error(error.message);
  }
};

export default createTrackFromKML;
